import React, { useState } from 'react';
import moment from 'moment';
import CredentialModal from '../../CredentialModal';
import { PRIORITY_FILTER } from 'components/AthleticTrainerModal/credentialHelpers';

const BOC_TYPE = 'national_certification';
const PLI_TYPE = 'proof_of_professional_liability_insurance';
const STATE_TYPE = 'state_certification_or_license';

// A credential is considered active while it has no expiry or its expiry is today/in the future.
const isActive = cred => !cred.valid_through || moment(cred.valid_through).isSameOrAfter(moment(), 'day');

// Best-effort purchase date for a credential (used to break ties between active PLIs).
const purchaseDate = cred => cred.approved_on || cred.valid_through || null;

// State abbreviations a credential is valid in (states_valid may be a comma separated list).
const statesOf = cred =>
  (cred.states_valid || '')
    .split(',')
    .map(s => s.trim().toUpperCase())
    .filter(Boolean);

const byPriority = (a, b) => (PRIORITY_FILTER[a.type] || 20) - (PRIORITY_FILTER[b.type] || 20);

const CredentialsBlock = ({ at, credentials, usStates, jobState, onDownloadAll, onReportIssue }) => {
  const [moreOpen, setMoreOpen] = useState(false);

  // Filter out non-credential entries and empty BOC certifications (same logic as before).
  // Apprenticeship-graduate BOCs are kept even without a document/pages, since they
  // represent a status rather than an uploaded certificate.
  const base = (credentials || []).filter(
    c =>
      !['resume', 'education', 'work_experience'].includes(c.type) &&
      !(c.type === BOC_TYPE && !c.apprenticeship_graduate && !c.document && !c.pages.length)
  );

  // --- Required credentials ---------------------------------------------------
  // BOC certification. BOC certificates earned via apprenticeship
  // (apprenticeship_graduate) are surfaced under additional certifications
  // instead of occupying the required BOC slot.
  const boc = base.find(c => c.type === BOC_TYPE && !c.apprenticeship_graduate);

  // PLI: display a single active policy. If more than one is active, keep the
  // oldest purchase; fall back to any PLI if none are currently active.
  const plis = base.filter(c => c.type === PLI_TYPE);
  const activePlis = plis.filter(isActive);
  const pliPool = activePlis.length ? activePlis : plis;
  const selectedPli = pliPool
    .slice()
    .sort((a, b) => new Date(purchaseDate(a) || 0) - new Date(purchaseDate(b) || 0))[0];

  // State certification/license: pick the one covering the job's location.
  const stateCerts = base.filter(c => c.type === STATE_TYPE);
  const jobStateUp = (jobState || '').trim().toUpperCase();
  const matchedStateCert = jobStateUp ? stateCerts.find(c => statesOf(c).includes(jobStateUp)) : null;
  const selectedStateCert = matchedStateCert || stateCerts[0];

  const requiredCreds = [boc, selectedPli, selectedStateCert].filter(Boolean);

  // --- Additional certifications ---------------------------------------------
  // Everything else, including non-matching state certs, extra PLI policies, and
  // BOC certificates earned via apprenticeship (apprenticeship_graduate).
  const additionalCreds = base
    .filter(c => !requiredCreds.includes(c) || (c.type === BOC_TYPE && c.apprenticeship_graduate))
    .sort(byPriority);

  const renderRequiredItem = (cred, idx) => (
    <div key={idx} className="credentials-block__item">
      <div className="credentials-block__item-info">
        <CredentialModal credential={cred} at={at} usStates={usStates} />
      </div>
    </div>
  );

  const renderAdditionalItem = (cred, idx) => {
    if (cred.type === 'npi_number' && at.npiNumber) {
      return (
        <div key={idx} className="credentials-block__secondary-item">
          <div>
            <div className="credentials-block__secondary-name">NPI Number</div>
            <div className="credentials-block__secondary-meta">{at.npiNumber}</div>
          </div>
        </div>
      );
    }
    if (cred.type === 'nata_number' && at.nataNumber) {
      return (
        <div key={idx} className="credentials-block__secondary-item">
          <div>
            <div className="credentials-block__secondary-name">NATA Number</div>
            <div className="credentials-block__secondary-meta">{at.nataNumber}</div>
          </div>
        </div>
      );
    }
    return (
      <div key={idx} className="credentials-block__secondary-item">
        <div className="credentials-block__secondary-item-content">
          <CredentialModal credential={cred} at={at} usStates={usStates} />
        </div>
      </div>
    );
  };

  return (
    <div className="credentials-block">
      <div className="at-card__header at-card__header--gray">VERIFY CREDENTIALS</div>
      <div className="credentials-block__body">
        {requiredCreds.map((cred, idx) => renderRequiredItem(cred, idx))}

        {additionalCreds.length > 0 && (
          <>
            <div className="credentials-block__toggle" onClick={() => setMoreOpen(!moreOpen)}>
              <span className="material-icons-outlined" style={{ fontSize: 18, color: '#3A9C95' }}>
                {moreOpen ? 'expand_less' : 'expand_more'}
              </span>
              <span className="credentials-block__toggle-label">
                {moreOpen ? 'Hide' : 'Show'} additional certifications
              </span>
              <span style={{ flex: 1 }} />
              <span className="credentials-block__toggle-count">{additionalCreds.length}</span>
            </div>

            <div
              className={`credentials-block__secondary${
                moreOpen ? '' : ' credentials-block__secondary--hidden'
              }`}
            >
              {additionalCreds.map((cred, idx) => renderAdditionalItem(cred, idx))}
            </div>
          </>
        )}

        <div className="credentials-block__actions">
          <a
            href="#modal"
            className="credentials-block__action-link"
            onClick={e => {
              e.preventDefault();
              onDownloadAll();
            }}
          >
            DOWNLOAD ALL
          </a>
          <a
            href="#modal"
            className="credentials-block__action-link credentials-block__action-link--muted"
            onClick={e => {
              e.preventDefault();
              onReportIssue();
            }}
          >
            REPORT ISSUE
          </a>
        </div>
      </div>
    </div>
  );
};

export default CredentialsBlock;
